"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Droplets, Sparkles, RotateCcw, ShieldCheck, Gauge, ArrowRight, CircleCheck as CheckCircle2, Factory, Layers, Zap } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const benefits = [{
  icon: Droplets,
  title: "Poupança de água e químicos",
  description: "Impacto mecânico otimizado que reduz o volume de água e agentes de limpeza até 40%."
}, {
  icon: Zap,
  title: "Ciclos de lavagem mais curtos",
  description: "Menor tempo de paragem de produção com cobertura completa e higienização acelerada."
}, {
  icon: ShieldCheck,
  title: "Conformidade sanitária e CIP",
  description: "Equipamentos certificados para requisitos rigorosos nos setores alimentar, farmacêutico e químico."
}, {
  icon: RotateCcw,
  title: "Repetibilidade e fiabilidade",
  description: "Resultados consistentes a cada ciclo, eliminando falhas manuais e garantindo rastreabilidade."
}];
const applications = [{
  title: "Lavagem de cubas e reatores",
  category: "Processamento Industrial",
  description: "Cabeçotes rotativos e estáticos para higienização completa sem necessidade de entrada humana.",
  specs: "Tanques até 30m de diâmetro",
  link: "/TankCleaningPage"
}, {
  title: "Sistemas CIP automatizados",
  category: "Setor Alimentar & Bebidas",
  description: "Integração precisa de coletores e bicos para limpeza em circuito fechado sem desmontagem.",
  specs: "Higienização 3D de alto impacto",
  link: "/AutomatedSprayingSystemsPage"
}, {
  title: "Desinfeção e sanitização de superfícies",
  category: "Higiene Fabril",
  description: "Atomização fina e uniforme para descontaminação de tapetes transportadores e câmaras.",
  specs: "Cobertura homogénea sem encharcamento",
  link: "/DisinfectionSystemsPage"
}, {
  title: "Lavagem a alta pressão",
  category: "Manutenção & Pesados",
  description: "Bicos cerâmicos e de carboneto de tungsténio para remoção de resíduos e incrustações tenazes.",
  specs: "Pressões até 500 bar",
  link: "/HighPressureNozzlesPage"
}];
export default function CleaningOverview() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="cleaningoverview" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 text-secondary border-secondary/20 bg-secondary/10 px-3 py-1 font-medium">
            <Sparkles className="size-3.5 mr-1.5 text-secondary" />
            Engenharia de Pulverização Aplicada
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground leading-tight">Porque a limpeza por pulverização é importante ?</h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">A limpeza e a lavagem afetam as operações em todas as etapas. Uma limpeza adequada melhora a qualidade do produto, a segurança dos trabalhadores, a vida útil dos equipamentos e a eficiência operacional.</p>
        </div>

        {/* Highlight Banner / Image & Summary Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center mb-20">
          <div className="lg:col-span-7">
            <div className="relative rounded-2xl overflow-hidden shadow-lg border border-border bg-muted">
              <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/industrial_cleaning_systems_overview.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/industrial_cleaning_systems_overview.png-wvc-sizes" alt="Tecnologia de lavagem industrial e cabeçotes de impacto Eurocontrol" loading="lazy" decoding="async" className="w-full h-auto object-cover aspect-16/10" src="https://eurocontrol.pt/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-14.28.24.png" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent opacity-80" />
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-primary-foreground">
                <p className="text-xs sm:text-sm font-medium tracking-wide uppercase text-primary-foreground/80">TECNOLOGIA TANKJET</p>
                <p className="text-lg sm:text-xl font-semibold mt-1">
                  Máxima eficiência na remoção de sujidade com caudal controlado
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-between gap-6">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-secondary">
                <Factory className="size-4" />
                <span>Otimização de Processos</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
                Menos recursos, maior higiene comprovada
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">Os métodos manuais podem ser inconsistentes, intensivos em mão de obra e gerar desperdícios desnecessários. Isto torna-se especialmente evidente quando a água, os produtos químicos e o tempo de limpeza não são bem controlados. As soluções de pulverização de engenharia melhoram a limpeza ao proporcionar o padrão de pulverização, o impacto e a cobertura adequados para a superfície ou recipiente-alvo.</p>
            </div>

            <div className="bg-card border border-border rounded-xl p-5 sm:p-6 shadow-xs">
              <div className="flex items-start gap-4">
                <div className="p-2.5 rounded-lg bg-accent text-accent-foreground shrink-0">
                  <Gauge className="size-5" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground">Diagnóstico e dimensionamento técnico</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                    Os nossos engenheiros analisam pressão, caudal, tipo de fluido e tempo de contacto para especificar a tecnologia ideal.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs">
                <Link to="/ContactsPage">
                  <span>Pedir estudo técnico</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border">
                <Link to="/TankCleaningPage">
                  <span>Ver lavagem de tanques</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="mb-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-secondary font-semibold">Vantagens operacionais</p>
              <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground mt-1">
                Porque atualizar o seu sistema de lavagem
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Garantia de conformidade higiénica com redução direta dos custos operacionais fabris.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, i) => {
            const IconComp = benefit.icon;
            return <motion.div key={benefit.title} variants={itemVariants} data-index={i}>
                  <Card className="h-full border border-border bg-card shadow-xs hover:border-secondary/30 transition-[border-color,box-shadow] duration-200">
                    <CardHeader className="space-y-3">
                      <div className="size-11 rounded-lg bg-accent text-accent-foreground flex items-center justify-center">
                        <IconComp className="size-5" />
                      </div>
                      <CardTitle className="text-lg font-bold text-foreground">
                        {benefit.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                        {benefit.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        <Separator className="my-12 bg-border" />

        {/* Key Use Cases / Applications */}
        <div>
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-secondary font-semibold">Aplicações principais</p>
              <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground mt-1">
                Soluções configuradas para cada processo
              </h3>
            </div>
            <Button asChild variant="ghost" className="text-secondary hover:text-secondary/90 w-fit">
              <Link to="/ApplicationsPage">
                <span>Ver todas as aplicações</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {applications.map((app, i) => <div key={app.title} data-index={i} className="flex">
                <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-200">
                  <CardHeader>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-xs font-semibold text-secondary tracking-wide uppercase">
                        {app.category}
                      </span>
                      <Layers className="size-4 text-muted-foreground" />
                    </div>
                    <CardTitle className="text-base sm:text-lg font-bold text-foreground leading-snug">
                      {app.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {app.description}
                    </CardDescription>
                    <div className="flex items-center gap-2 text-xs font-medium text-foreground bg-muted/60 p-2.5 rounded-md border border-border/50">
                      <CheckCircle2 className="size-3.5 text-secondary shrink-0" />
                      <span>{app.specs}</span>
                    </div>
                  </CardContent>
                  <CardFooter className="border-t border-border pt-4">
                    <Button asChild variant="link" className="px-0 h-auto text-secondary hover:text-secondary/80 text-sm font-semibold">
                      <Link to={app.link}>
                        <span>Consultar tecnologia</span>
                        <ArrowRight className="size-3.5 ml-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>)}
          </div>
        </div>

        {/* Bottom Consultation Strip */}
        <div className="mt-16 bg-muted border border-border rounded-2xl p-6 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h4 className="text-xl font-bold text-foreground">
              Necessita de apoio no dimensionamento do seu sistema de lavagem?
            </h4>
            <p className="text-sm text-muted-foreground">
              A nossa equipa técnica em Portugal está disponível para testes, simulação de cobertura e auditorias no local.
            </p>
          </div>
          <div className="flex shrink-0">
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs">
              <Link to="/ContactsPage">
                <span>Fale com um especialista</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}